// Party page (spec §6.6).
//
// Sectioned layout, ONE ORDER on both devices, money first (sweep §5.17,
// 14 Sept 2026): Balances · Statement · Settle later · Running account ·
// Recent deals, then Identity · Wallets · Quick actions · Notes. Below lg the
// sections stack in that order; lg+ arranges them in two console columns —
// money on the left, the rest on the right.
//
// Minimised 14 Sept 2026 (sweep §5.17 / item 57):
//   · badges that say "nothing special" are gone — Active, Tier · Standard and
//     KYC · None (83 of 95 parties) show only when not the default;
//   · Entity and Created fold into one muted line under the name;
//   · the Telegram row shows only when set (0 of 183), Credit limit only when
//     above zero (0 of 183), Compliance is no longer a section;
//   · one SignLaw (the Statement prints none of its own);
//   · the running-account rule paragraphs, the "ridden in" explanation and the
//     promote paragraph live in "About this screen"; Promote is a link in the
//     Type row; Running account is one line and one button;
//   · Recent deals prints the rendered count with "All trades →"; a deal line
//     is the console's one deal row (components/Ledger DealRowLine, 17 Sept
//     2026): stamp · party · pays → receives · @ rate, customer-side;
//   · Notes hide the migration provenance lines and fold closed;
//   · Quick actions are a wrapping row: Receive · Pay · Adjust balance — the
//     Settle later screen's words — and the footer sentence is gone;
//   · an OWN ACCOUNT (99 parties) has no box: no Balances, no Recent deals,
//     no "Pay". It shows Identity · Parent group · Statement (the money-
//     movements door) · Wallets, and its actions are the three account doors.
//   · One KeyValue (the shared one), one Pill.
//
// 21 Oct 2026 audit (operator: "I need clean view for operator"): a failed read
// keeps the page's head (title and Back) over its one error line; the muted line
// names the desk only when it is not DubaiOTC; the Settle later card has no
// "Settle later →" link beside its own title; Identity's Name row stands only
// when it adds something ("Stored as …", the review note); a wallet says its
// state only when it is NOT in use (Held, Retired, Archived), and "Retire or
// archive an address in the Wallet register." is in About this screen, the card
// keeping the door alone. NOT changed here — both wait on the operator's word:
// the order (the Statement directly under Balances, so the tie is on screen with
// the box it ties to — sweep §5.17, 14 Sept 2026) and the Recent deals card.
//
// 22 Oct 2026 design-system sweep: a failed read is ONE red line (what failed, Try again, the server's sentence a tap away) under
// the page's head; the name in the title WRAPS (a 37-character merchant read "Northern Gulf Commodi…" over the page's own title);
// the empty lines are the kit's EmptyState; the Ledger-key line is six words with its explanation a tap away; the quick actions
// and the Running-account button are 44 px targets on a phone. The ORDER and every door are untouched.

import { useState } from 'react';
import { errorWords, readWords } from '../lib/errors';
import { useParams, useSearchParams, useNavigate, Link, Navigate } from 'react-router-dom';
import { isOwnAccount as isOwnParty, treasuryHref } from '../lib/ownAccounts';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tg } from '../lib/telegram';
import {
  IdCard, Wallet, Coins, Activity, StickyNote, ArrowLeftRight, Handshake, Repeat,
} from 'lucide-react';
import { api } from '../lib/api';
import { SettleButton, legWords } from '../components/SettleButton';
import { displayAsset, fmtAmount, fmtDay, fmtRelative, smartCase } from '../lib/format';
import {
  partyName, partySubtitle, needsNameReview, compareRelevance,
  partyTypeLabel, trustTierLabel, dustFor, carriedRodeWords,
  RUNNING_ACCOUNT_TITLE, RUNNING_ACCOUNT_RULE, RUNNING_ACCOUNT_OFF_RULE, RUNNING_ACCOUNT_BALANCE_NOTE,
} from '../lib/naming';
import { Section } from '../components/Section';
import { KeyValue, KeyValueRow } from '../components/KeyValue';
import { type PillTone } from '../components/Pill';
import { SignLaw } from '../components/SignLaw';
import { Pending, Pill, ErrorLine, StateLine, EmptyState } from '../components/kit';
import { Field } from '../components/Field';
import { ScreenHeader } from '../components/ScreenHeader';
import { PartyStatement, StatementHelp } from '../components/PartyStatement';
import { DealRowLine, LEDGER } from '../components/Ledger';
import { GroupingPanel } from '../components/GroupingPanel';
import { useIsDesktop } from '../lib/useIsDesktop';
import { WALLET_CHAINS, chainIsRead, walletState, watchWords } from '../lib/walletStates';

/** WHAT HE PRESSES (design system §1, §4): body size, a 44 px target on a phone, centred. */
const TOUCH = 'text-body py-1.5 px-3 inline-flex items-center justify-center max-lg:min-h-11';

const TIER_TONE: Record<string, PillTone> = { new: 'neutral', standard: 'neutral', repeat: 'good', vip: 'warn' };
const KYC_TONE:  Record<string, PillTone> = { none: 'neutral', pending: 'warn', approved: 'good', rejected: 'bad' };
const STATUS_TONE: Record<string, PillTone> = { active: 'good', inactive: 'warn', archived: 'neutral' };

// Sentence-case text — a raw enum never renders. (Trust tier lives in
// naming.ts as trustTierLabel(): the new-party form sets the tier and was
// printing the raw enum, so the two share one table.)
const STATUS_TEXT: Record<string, string> = { active: 'Active', inactive: 'Inactive', archived: 'Archived' };
const KYC_TEXT:    Record<string, string> = { none: 'None', pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };
const ENTITY_TEXT: Record<string, string> = { dubaiotc: 'DubaiOTC', indiaotc: 'IndiaOTC', euotc: 'EuOTC' };

// Why a stored name is flagged. The name itself is never rewritten or masked
// here — this screen is where the operator decides what it should say.
const REVIEW_NOTE: Record<'profanity' | 'ambiguous' | 'empty', string> = {
  profanity: 'Flagged for review — shown exactly as stored, never auto-renamed.',
  ambiguous: 'Ambiguous — too little to identify. Rename it when you know who.',
  empty:     'No stored name — this is the books’ own label for the record.',
};

/** Notes the migration wrote, not the operator (47 of 63 non-own parties):
 *  "Imported from legacy admin…", "Created 2026-…". Not operator-facing. */
const PROVENANCE = /^(Imported from legacy|Created 20\d\d-)/;

export function PartyDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const entity = search.get('entity') ?? 'dubaiotc';
  const nav = useNavigate();

  const q = useQuery({
    queryKey: ['party', slug, entity],
    queryFn:  () => api.partyDetail(slug!, entity),
    enabled:  !!slug,
  });
  const isDesktop = useIsDesktop();
  const qc = useQueryClient();

  // PROMOTE TO MERCHANT — the label and the money move together.
  //
  // customer:{e}:{slug} and counterparty:{e}:{slug} are different accounts. The
  // server moves any open position across BEFORE it relabels, because relabelling
  // alone would leave the balance behind and split the party in two — the exact
  // 2026-08-22 failure. Offered on every customer; the server refuses anything
  // that is not one.
  //
  // HOISTED above the loading returns (2 Sept): hooks below an early return
  // change the hook count between renders — this screen crashed the whole
  // console on every load until the boundary caught it. Every hook in this
  // component now runs unconditionally, before any return.
  const promote = useMutation({
    mutationFn: () => api.promoteParty(slug!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['party', slug] });
      qc.invalidateQueries({ queryKey: ['parties'] });
      tg()?.HapticFeedback.notificationOccurred('success');
    },
  });

  // SETTLE LATER COMES FROM /settle, NOT FROM THIS PAGE'S DEAL LIST.
  // GET /api/parties/:slug returns the last twenty deals, so a merchant who
  // had traded twenty times since an open settle-later trade saw a section
  // that read as complete and did not name the trade the settle screen was
  // still showing. This is the same read Home and /settle use, so the three
  // agree by construction — and it carries the per-leg verdicts, which the
  // party payload does not. The key carries the entity: Home and /settle cache
  // under ['settlements'], and reading another entity's books into that key
  // would hand them somebody else's parties. refreshMoneyViews invalidates by
  // prefix, so this still refreshes with everything else after a posting.
  const settle = useQuery({
    queryKey: ['settlements', entity],
    queryFn: () => api.settlements(entity),
    refetchOnWindowFocus: true,
  });

  // AN OWN ACCOUNT'S MONEY IS ON THE ACCOUNT. The Record in / out door wants
  // the account AND its asset (?rail= & ?railAsset=), and the party payload
  // knows neither — the treasury read does. Same key Treasury uses, so it is
  // usually a cache hit; asked for only on an own-account page.
  const ownSlug = q.data?.party.party_type === 'own_account' ? (q.data.party.treasury_slug ?? null) : null;
  const treasury = useQuery({
    queryKey: ['treasury', 'balances', entity],
    queryFn:  () => api.treasuryBalances(entity as 'dubaiotc' | 'indiaotc'),
    enabled:  !!ownSlug,
    staleTime: 60_000,
  });

  // A FAILED READ KEEPS THE PAGE'S HEAD (21 Oct 2026 audit): it used to replace
  // the whole page with one red line — no title, no way back. The name is the
  // slug cleaned up the way every list does it (no read, so no stored name); the
  // line itself is the same, and it is still the only thing under the head — a
  // failed read is never drawn as an empty party.
  if (q.isError) return (
    <div className="space-y-3">
      <div className="space-y-1">
        <button onClick={() => nav(-1)} className="btn-link text-meta max-lg:p-3 max-lg:-m-3">← Back</button>
        <ScreenHeader title={partyName({ slug })} />
      </div>
      <StateLine
        tone="bad"
        more={readWords(q.error)}
        action={<button type="button" className="btn-link text-body" disabled={q.isFetching} onClick={() => q.refetch()}>{q.isFetching ? 'Trying…' : 'Try again'}</button>}
      >
        Couldn&rsquo;t read the party.
      </StateLine>
    </div>
  );
  // A DESK ACCOUNT HAS NO PAGE HERE (operator, 21 Oct 2026: "in party, I am still seeing treasury accounts, which it
  // should't display and anyway it's wrong"): its money, its history and its wallets are on the account — Treasury opens it,
  // from a typed address, an old link or a bookmark alike. No hook is added below this line; the own-account branches
  // further down are unreachable now and go at the end-of-phase cleanup.
  if (q.data && isOwnParty(q.data.party)) return <Navigate to={treasuryHref(q.data.party.treasury_slug)} replace />;
  // !q.data, not isLoading: react-query v5 keeps isLoading false for a
  // disabled/retrying query, and destructuring undefined blacked out the WHOLE
  // console (2 Sept — no screen may ever assume data by flag alone).
  //
  // ONE FIRST PAINT (operator, 17 Sept 2026: "this party screen gets changed
  // after opening in few seconds"). The page used to paint when the party
  // read landed, then Settle later popped in when the settlements read landed
  // and pushed Running account and everything under it down. So the page
  // waits for BOTH reads: the settle read counts as landed once it has data
  // OR has failed — a failed read still shows the page, with its error line
  // in the Settle later section, as before. The Statement below the fold
  // keeps its own "Reading…"; nothing else appears late.
  if (!q.data || settle.isLoading) return <Pending />;

  const { party, balances, wallets, deals, children } = q.data;
  // THE RUNNING ACCOUNT (db/0036). `carried_on_account` is sent ONLY for a
  // party the operator has marked; for anybody else it is null and this page
  // says nothing about an arrangement that does not exist. Its `balance` is not
  // read here on purpose — it is the same `balances` above, derived from them
  // server-side, and printing it twice is how one position starts reading as
  // two. What is genuinely new is `rode`: the part of this balance that arrived
  // by trades closing with a remainder rather than being paid.
  const carried = q.data.carried_on_account ?? null;
  const ridden  = carried ? carriedRodeWords(carried.rode) : '';
  const ridesOnAccount = party.trade_remainder_rides_on_account === true;
  const isOwnAccount = party.party_type === 'own_account';
  const isGroup      = isOwnAccount && !party.treasury_slug;
  const isMerchant   = party.party_type === 'counterparty';

  // One name, one subtitle, same helpers the lists use — a party must read the
  // same here as it does in Parties, TradesLog and the command palette.
  // Relevance sees this party's real balances and its latest deal, so the
  // State slot ("Dormant" / "3d ago") is computed, never guessed.
  const lastDealAt = deals[0]?.booked_at ?? null;
  const relevant   = { ...party, balances, last_activity_at: lastDealAt };
  const title      = partyName(party);
  const review     = needsNameReview(party.display_name ?? '');
  // The stored string is shown, never silently replaced — this is the one
  // screen where the operator can act on it.
  const storedDiffers = (party.display_name ?? '').trim() !== title;
  // Entity and Created, folded into the one muted line under the name.
  // 21 Oct 2026 audit: one desk now, so "DubaiOTC" on every page said nothing —
  // the desk is named only when it is some other one (a closed desk's old party).
  const otherDesk = party.entity && party.entity !== 'dubaiotc'
    ? `${ENTITY_TEXT[party.entity] ?? smartCase(party.entity)} · ` : '';
  const stamp = `${otherDesk}created ${fmtDay(party.created_at)}`;
  const subtitle = `${partySubtitle(relevant, { compact: !isDesktop })} · ${stamp}`;

  /* ─── The pills that mean something ────────────────────────── */
  // Only when NOT the default: "Active · Standard · None" on 83 of 95 parties
  // said nothing. An own account carries no tier and no KYC.
  const pills: Array<{ key: string; tone: PillTone; text: string }> = [];
  if (party.status && party.status !== 'active') {
    pills.push({ key: 'status', tone: STATUS_TONE[party.status] ?? 'neutral', text: STATUS_TEXT[party.status] ?? smartCase(party.status) });
  }
  if (!isOwnAccount && party.trust_tier && party.trust_tier !== 'standard') {
    pills.push({ key: 'tier', tone: TIER_TONE[party.trust_tier] ?? 'neutral', text: `Tier · ${trustTierLabel(party.trust_tier)}` });
  }
  if (!isOwnAccount && party.kyc_status && party.kyc_status !== 'none') {
    pills.push({ key: 'kyc', tone: KYC_TONE[party.kyc_status] ?? 'neutral', text: `KYC · ${KYC_TEXT[party.kyc_status] ?? smartCase(party.kyc_status)}` });
  }
  if (ridesOnAccount) pills.push({ key: 'ride', tone: 'info', text: RUNNING_ACCOUNT_TITLE });

  /* ─── Identity ──────────────────────────────────────────────── */
  /* Internal identifiers are NOT shown to the operator — they're DB plumbing.
     The operator-facing identity is the name and the type; Telegram and the
     credit limit only when set. Promote is a link in the Type row. */
  // The card stands only when it says something the header above does not
  // — the name and the type are the title and the subtitle already; a desk
  // account's Identity card was those two words twice (20 Sept 2026).
  const identityAdds = storedDiffers || !!review || !!party.telegram_username
    || Number(party.credit_limit_usd ?? 0) > 0 || party.party_type === 'customer'
    || promote.isError || promote.isSuccess;
  const identitySection = !identityAdds ? null : (
    <Section title="Identity" icon={<IdCard size={14} />}>
      <KeyValue>
        {/* THE NAME ROW ONLY WHEN IT CARRIES SOMETHING (21 Oct 2026 audit): it was
            the heading's own words again on every customer. "Stored as …" and the
            review note are what it adds — and they are why this is the one screen
            where the operator can act on the stored string. */}
        {(storedDiffers || review) && (
          <KeyValueRow
            label="Name"
            value={
              <>
                <span>{title}</span>
                {storedDiffers && (
                  <span className="block text-meta text-tg-hint mt-0.5">
                    Stored as <span className="font-mono">{party.display_name}</span>
                  </span>
                )}
                {review && (
                  <span className="block text-meta text-brand-muted mt-0.5">{REVIEW_NOTE[review]}</span>
                )}
              </>
            }
          />
        )}
        <KeyValueRow
          label="Type"
          value={
            <>
              <span>{isGroup ? 'Group' : partyTypeLabel(party.party_type)}</span>
              {/* THE NATIVE CONFIRM (the pattern to copy): a one-tap write
                  that moves money and relabels says what it will do first. */}
              {party.party_type === 'customer' && (
                <button
                  type="button"
                  disabled={promote.isPending}
                  className="btn-link text-meta ml-2 max-lg:py-2.5 max-lg:-my-2.5 disabled:opacity-60"
                  onClick={() => {
                    if (confirm(`Promote ${title} to merchant?\n\nAny open balance moves to their merchant account first, then the type changes. This is recorded in the audit log.`)) {
                      promote.mutate();
                    }
                  }}
                >
                  {promote.isPending ? 'Promoting…' : 'Promote to merchant'}
                </button>
              )}
              {promote.isError && <ErrorLine>{errorWords(promote.error)}</ErrorLine>}
              {promote.isSuccess && (
                <span className="block text-meta text-pos mt-0.5">
                  Now a merchant{promote.data?.moved?.length ? ` — moved ${promote.data.moved.join(', ')}` : ''}.
                </span>
              )}
            </>
          }
        />
        {party.telegram_username && (
          <KeyValueRow label="Telegram" value={<code className="font-mono text-meta">@{party.telegram_username}</code>} />
        )}
        {Number(party.credit_limit_usd ?? 0) > 0 && (
          <KeyValueRow label="Credit limit" value={<span className="amt">{fmtAmount(party.credit_limit_usd, 'AED')} USD-eq</span>} />
        )}
      </KeyValue>
    </Section>
  );

  /* ─── Group membership (own_account only) ─────────────────────── */
  const groupingSection = (
    <GroupingPanel
      partyType={party.party_type}
      slug={party.slug}
      isGroup={isGroup}
      parentSlug={party.parent_slug ?? null}
      parentDisplayName={party.parent_display_name ?? null}
      children={children}
    />
  );

  /* ─── Balances (not on an own account — its money is on the account) ── */
  const balancesSection = isOwnAccount ? null : (
    <Section title="Balances" icon={<Coins size={14} />}>
      {Object.keys(balances).length === 0 ? (
        <EmptyState className="!py-2">Square in every asset.</EmptyState>
      ) : (
        <>
          <KeyValue>
            {/* Ordered by the shared comparator, not alphabetically: the asset
                that matters is the one with money on it, and the per-asset dust
                floor keeps a 0.5 BTC line above a dusty AED one. */}
            {Object.entries(balances)
              .map(([asset, balance]) => ({ asset, balance }))
              .sort(compareRelevance)
              .map(({ asset, balance }) => (
                <KeyValueRow
                  key={asset}
                  label={displayAsset(asset)}
                  value={<span className={`amt-key ${balance < 0 ? 'text-neg' : ''}`}>{fmtAmount(balance, asset)}</span>}
                />
              ))}
          </KeyValue>
          {/* ONE WORDING FOR ONE LAW, once on the page — the Statement below
              prints none of its own. */}
          <SignLaw className="pt-2" />
        </>
      )}
      {/* DECISION 4 — what rode in, named where the money is. One line; what
          it means is in About this screen. Outside the zero test on purpose:
          a party who has settled up still has an arrangement. */}
      {ridden && (
        <p className="text-meta text-tg-hint pt-2 mt-2 border-t border-tg-secondary">
          Ridden in: <span className="text-tg-text tabular">{ridden}</span>
        </p>
      )}
    </Section>
  );

  /* ─── Statement ───────────────────────────────────────────── */
  // HOW THE BOX ABOVE WAS ARRIVED AT. It sits immediately under Balances on
  // the phone and directly under the two short columns at lg+, because its
  // top row IS that box — the tie is only convincing if both numbers are on
  // screen at once. Its own component, so its hooks are its own and this
  // screen's hoisted-hook rule stays simple.
  const statementSection = (
    <PartyStatement
      slug={party.slug}
      entity={party.entity ?? entity}
      balances={balances}
      partyType={party.party_type}
      treasurySlug={party.treasury_slug}
    />
  );

  /* ─── Wallets ─────────────────────────────────────────────── */
  const walletsSection = <WalletsSection partySlug={party.slug} wallets={wallets} keys={q.data.wallet_keys ?? []} />;

  /* ─── Settle later ─────────────────────────────────────────── */
  // Settle-later trades still open on this party. The box above is what stands
  // between you; these rows are why. "Mark settled" is the operator's word; the
  // sweep on /settle closes a trade by itself once each leg has been received,
  // paid, netted — or, for a party carrying a running account, once money has
  // moved on the leg and the rest rides (legWords prints that leg as "INR on
  // account · 15,705 rode").
  const awaitingDeals = settle.data?.parties.find((p) => p.slug === party.slug)?.deals ?? [];
  const awaitingSection = (!settle.isError && awaitingDeals.length === 0) ? null : (
    // 21 Oct 2026 audit: no "Settle later →" link beside the title "Settle later"
    // (it restated the card; the sidebar and Home are the doors to the screen).
    <Section
      title="Settle later"
      icon={<Handshake size={14} />}
    >
      {/* A FAILED READ IS NOT AN ALL-CLEAR. A section that quietly disappears
          because the books did not answer tells the operator this merchant has
          nothing open, which is the one thing it must never say. */}
      {settle.isError
        ? (
          <StateLine
            tone="bad"
            more={readWords(settle.error)}
            action={<button type="button" className="btn-link text-body" disabled={settle.isFetching} onClick={() => settle.refetch()}>{settle.isFetching ? 'Trying…' : 'Try again'}</button>}
          >
            Couldn&rsquo;t read settle-later trades.
          </StateLine>
        ) : (
          // THE ONE DEAL ROW (components/Ledger, 17 Sept 2026), the same as
          // Recent deals below: stamp · legs · @ rate · Awaiting, with Mark
          // settled in the row's action seat and the legs' state on a line
          // beneath — not a third deal format on the page.
          <div className={`-mx-2 divide-y divide-tg-secondary ${LEDGER.block}`}>
            {awaitingDeals.map((d) => (
              <DealRowLine
                key={d.deal_id}
                deal={{ ...d, party_name: d.party_name ?? party.display_name, party_slug: d.party_slug ?? party.slug }}
                hideParty
                hideState
                // The party travels with the button so its refusal panel can
                // offer the exact shortfall as one tap; the panel opens in
                // the button's seat, which is the row's slack.
                action={<SettleButton dealId={d.deal_id} partySlug={party.slug} />}
                detail={d.legs && (
                  <div className="text-meta">
                    <span className={d.legs.take.settled ? 'text-pos' : 'text-warn'}>{legWords(d.legs.take, 'take')}</span>
                    <span className="text-tg-hint"> · </span>
                    <span className={d.legs.give.settled ? 'text-pos' : 'text-warn'}>{legWords(d.legs.give, 'give')}</span>
                  </div>
                )}
              />
            ))}
          </div>
        )}
    </Section>
  );

  /* ─── Running account ─────────────────────────────────────── */
  // DECISION 1 NEEDS A DOOR. "Only a party the operator has marked behaves this
  // way" is only true if there is somewhere to mark one, and this is the page
  // that knows who the party is. Under Settle later in both layouts because it
  // is the rule that decides how those rows close. NOT ON AN OWN ACCOUNT: a
  // desk account does not run an account with the desk.
  const runningAccountSection = isOwnAccount ? null : (
    <RunningAccountSection
      slug={party.slug}
      entity={party.entity ?? entity}
      name={title}
      on={ridesOnAccount}
    />
  );

  /* ─── Recent deals (not on an own account) ─────────────────── */
  // The header prints the RENDERED count and "All trades →" opens the log on
  // this party (4 parties have more than 20 deals; the payload holds 20).
  const recent = deals.slice(0, 10);
  const dealsSection = isOwnAccount ? null : (
    <Section
      title="Recent deals"
      icon={<Activity size={14} />}
      accessory={
        <span className="flex items-center gap-2">
          <span className="text-tg-hint">{recent.length}</span>
          <Link to={`/trades?party=${encodeURIComponent(party.slug)}`} className="text-tg-link text-meta max-lg:p-2.5 max-lg:-m-2.5">All trades →</Link>
        </span>
      }
    >
      {/* THE ONE DEAL ROW (components/Ledger, 17 Sept 2026): stamp · buys /
          sells … pays … · @ rate, on the same seats Home's recent trades
          use — without the party, whose name is this page's title; capped
          at LEDGER.block so the figures sit near the stamp on a wide
          monitor. A plain row — nothing opens from here; All trades → is
          the door to the log. */}
      {recent.length === 0
        ? <EmptyState className="!py-2">No deals yet.</EmptyState>
        : (
          <div className={`-mx-2 divide-y divide-tg-secondary ${LEDGER.block}`}>
            {recent.map((d) => (
              <DealRowLine
                key={d.deal_id}
                deal={{ ...d, party_name: d.party_name ?? party.display_name, party_slug: d.party_slug ?? party.slug }}
                hideParty
              />
            ))}
          </div>
        )}
    </Section>
  );

  /* ─── Notes — provenance hidden, folded closed ─────────────── */
  const noteLines = (party.notes ?? '').split('\n').filter((l) => l.trim() && !PROVENANCE.test(l.trim()));
  const notesSection = noteLines.length > 0 ? (
    <details className="card">
      <summary className="font-semibold text-body cursor-pointer select-none max-lg:py-2">
        <StickyNote size={14} className="inline-block opacity-70 mr-1.5 -mt-0.5" />Notes
      </summary>
      <p className="text-body whitespace-pre-wrap pt-2">{noteLines.join('\n')}</p>
    </details>
  ) : null;

  /* ─── Quick actions ────────────────────────────────────────── */
  // WHICH WAY THIS PARTY POINTS — PER ASSET, NEVER AS ONE NUMBER. AED, INR
  // and USDT are not the same money, and the settle-later shape is precisely
  // two boxes pointing opposite ways. Positive is the party owing the desk;
  // negative is the desk owing them; each asset answers for itself, past its
  // own dust floor. Both moves are always offered; the one this box calls for
  // is listed first.
  const boxes    = Object.entries(balances ?? {}).filter(([a, v]) => Math.abs(v ?? 0) > dustFor(a));
  const theyOwe  = boxes.some(([, v]) => v > 0);
  const deskOwes = boxes.some(([, v]) => v < 0);
  const pq = `party=${encodeURIComponent(party.slug)}`;
  const ent = entity === 'dubaiotc' ? '' : `entity=${entity}`;
  const receiveAction = { to: `/record?${pq}&dir=in`,  label: 'Receive' };
  const payAction     = { to: `/record?${pq}&dir=out`, label: 'Pay' };
  const [settleAction, otherAction] = deskOwes && !theyOwe
    ? [payAction, receiveAction]
    : [receiveAction, payAction];

  // An own account's doors write to the ACCOUNT (§5.17): all three carry the
  // account and its asset through the one ?rail= ?railAsset= contract — Record
  // money, Move money and Adjust balance each read it — once the treasury
  // read has named the asset; before that, the bare screen. The desk rides
  // along as ?entity= off the default desk, as the party doors carry it.
  const ownRail = ownSlug
    ? (treasury.data?.rows.find((r) => r.slug === ownSlug && r.balance !== 0)
      ?? treasury.data?.rows.find((r) => r.slug === ownSlug))
    : undefined;
  const onAccountParams = [
    ...(ownSlug && ownRail?.asset ? [`rail=${encodeURIComponent(ownSlug)}`, `railAsset=${encodeURIComponent(ownRail.asset)}`] : []),
    ...(ent ? [ent] : []),
  ];
  const onAccount = onAccountParams.length ? `?${onAccountParams.join('&')}` : '';

  const actions: Array<{ to: string; label: string }> = isOwnAccount
    ? [
        // Short: the card is on the account's own page, "this account" three
        // times over was the clutter (20 Sept 2026).
        { to: `/treasury/rebalance${onAccount}`, label: 'Move money' },
        { to: `/record${onAccount}`,             label: 'Record in / out' },
        { to: `/treasury/adjust${onAccount}`,    label: 'Adjust' },
      ]
    : [
        { to: `/trade?${pq}`, label: 'Book a trade' },
        settleAction,
        otherAction,
        { to: `/treasury/adjust?${pq}`, label: 'Adjust balance' },
        // THE TWO DOORS THAT TOUCH NO DESK ACCOUNT (11 Sept 2026). A balance
        // moved to another party, and a balance converted into another asset.
        // "Move to a party" is DubaiOTC-only since the wind-down (operator, 25
        // Sept 2026: "cut indiaotc") — RecordBetweenParties.tsx no longer has
        // a desk to send an IndiaOTC party to, so the door is not offered on
        // one; the link would otherwise search for `from` on the wrong desk
        // and silently find nobody.
        ...(party.entity !== 'indiaotc'
          ? [{ to: `/record?mode=p2p&from=${encodeURIComponent(party.slug)}${ent ? `&${ent}` : ''}`, label: 'Move to a party' }]
          : []),
        { to: `/parties/${encodeURIComponent(party.slug)}/convert${ent ? `?${ent}` : ''}`, label: 'Convert balance' },
        // MERCHANTS ONLY (12 Sept 2026, "Loki as sub-broker"): the trade's
        // legs park on a settle-later account, which only a merchant has.
        ...(isMerchant
          ? [{ to: `/parties/${encodeURIComponent(party.slug)}/sub-broker${ent ? `?${ent}` : ''}`, label: 'Sub-broker trade' }]
          : []),
      ];

  const quickActionsSection = (
    <Section title="Quick actions" icon={<ArrowLeftRight size={14} />}>
      {/* Two aligned columns on the phone — a ragged wrap of seven buttons
          read as clutter (20 Sept 2026); an odd last button takes the whole
          line rather than dangling. At lg+ the wrapping row (§5.17). */}
      <div className="grid grid-cols-2 gap-2 pt-1 [&>*:last-child:nth-child(odd)]:col-span-2 lg:flex lg:flex-wrap">
        {actions.map((a) => (
          <Link key={a.label} to={a.to} className={`btn-secondary ${TOUCH} text-center whitespace-nowrap`}>{a.label}</Link>
        ))}
      </div>
    </Section>
  );

  /* ─── About this screen ───────────────────────────────────── */
  const help = isOwnAccount ? (
    <>
      <p>
        A desk account: its money is on the account, not in a party box, so there is no balance
        to settle and no trade to book here. The Statement door opens its money movements; the
        actions write to the account itself.
      </p>
      <p>A group holds accounts; the money is on the accounts under it.</p>
    </>
  ) : (
    <>
      <p>
        Balances is the box — what stands between the desk and this party, per asset. Settle later
        lists this party&rsquo;s trades still open; Mark settled and its refusal are the Settle later
        screen&rsquo;s, in the same words.
      </p>
      <p>
        {RUNNING_ACCOUNT_TITLE} on: {RUNNING_ACCOUNT_RULE}
      </p>
      <p>
        {RUNNING_ACCOUNT_TITLE} off: {RUNNING_ACCOUNT_OFF_RULE}
      </p>
      {carried && (
        <p>
          Ridden in — {RUNNING_ACCOUNT_BALANCE_NOTE} The figure is a running total of what trades
          stopped asking for since the books opened — not a debt on top of the balance. A trade
          closed by hand with Mark settled anyway records none.
        </p>
      )}
      {party.party_type === 'customer' && (
        <p>
          Promote to merchant: a merchant carries a running position and can have trades agreed now
          and settled later. Any open balance moves across first, then the type changes; both are
          recorded in the audit log.
        </p>
      )}
      <p>
        Wallets: retire or archive an address in the Wallet register.
      </p>
      <StatementHelp />
    </>
  );

  const header = (
    <div className="space-y-1">
      <button onClick={() => nav(-1)} className="btn-link text-meta max-lg:p-3 max-lg:-m-3">← Back</button>
      <ScreenHeader
        title={
          <span className="inline-flex items-baseline gap-1.5 min-w-0 max-w-full">
            <span className="min-w-0 break-words" title={party.display_name ?? title}>{title}</span>
            {review && <ReviewMark reason={review} />}
          </span>
        }
        subtitle={subtitle}
        actions={pills.length > 0
          ? <>{pills.map((p) => <Pill key={p.key} tone={p.tone}>{p.text}</Pill>)}</>
          : undefined}
        help={help}
      />
    </div>
  );

  // ONE ORDER, MONEY FIRST (§5.17). Two columns at lg+, one stack below.
  //
  // THE STATEMENT TAKES THE WHOLE WIDTH at lg+ (15 Sept 2026). It is a table
  // with a balance column per asset — five for Loki — and in half the page
  // those columns squeezed the sentence to "Bought 53,947.00 USDT…", which is
  // the one thing on the row that says what moved. The two columns above it
  // are short (Balances beside Identity, Wallets, Quick actions), so Balances
  // is still on screen when the top row that ties to it is.
  const money = [balancesSection, awaitingSection, runningAccountSection];
  const rest  = [identitySection, groupingSection, walletsSection, quickActionsSection, notesSection];

  if (isDesktop) {
    return (
      <div className="space-y-4">
        {header}
        <div className="grid grid-cols-2 gap-4 items-start">
          <div className="space-y-4">{money}</div>
          <div className="space-y-4">{rest}</div>
        </div>
        {statementSection}
        {dealsSection}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {header}
      {balancesSection}
      {statementSection}
      {awaitingSection}
      {runningAccountSection}
      {dealsSection}
      {rest}
    </div>
  );
}

/** Muted review affordance — same vocabulary as <Name>'s flag. It marks the
 *  name, it never masks or replaces it. */
function ReviewMark({ reason }: { reason: 'profanity' | 'ambiguous' | 'empty' }) {
  return (
    <span
      role="img"
      title={REVIEW_NOTE[reason]}
      aria-label={REVIEW_NOTE[reason]}
      className="shrink-0 select-none text-meta leading-none text-tg-hint/70"
    >
      ⚑
    </span>
  );
}

/** SEE AND SET THE ARRANGEMENT — decision 1's door. One line and one button
 *  (§5.17); the rule's paragraphs are in About this screen. Not a silent
 *  toggle: turning this on changes when this party's trades close, so it is
 *  a decision, and the route records it in the audit log with a name and a
 *  date. The confirm names what will change. */
function RunningAccountSection({ slug, entity, name, on }: {
  slug: string; entity: string; name: string; on: boolean;
}) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (next: boolean) =>
      api.patchParty(slug, { entity: entity as 'dubaiotc' | 'indiaotc', trade_remainder_rides_on_account: next }),
    onSuccess: () => {
      tg()?.HapticFeedback.notificationOccurred('success');
      // The rule this changes is read by the settle sweep, so the settle views
      // have to be pulled in with the party — not just this card.
      qc.invalidateQueries({ queryKey: ['party', slug] });
      qc.invalidateQueries({ queryKey: ['parties'] });
      qc.invalidateQueries({ queryKey: ['settlements'] });
    },
    onError: () => tg()?.HapticFeedback.notificationOccurred('error'),
  });

  return (
    <Section title={RUNNING_ACCOUNT_TITLE} icon={<Repeat size={14} />}>
      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
        <p className="text-meta">
          {on ? 'On — remainders ride on this balance.' : 'Off — every leg must be paid in full.'}
        </p>
        <button
          type="button"
          disabled={m.isPending}
          onClick={() => {
            const q = on
              ? `Stop carrying remainders for ${name}?\n\nTheir trades will then stay open until every leg is paid in full. Nothing already carried moves — it stays on their balance. This is recorded in the audit log.`
              : `Carry remainders on a running account for ${name}?\n\nA trade leg of theirs will close as soon as money has moved on it, and the remainder — usually their own margin — will ride on their balance, recorded on the deal and in the audit log. A leg nothing has moved on still blocks.`;
            if (confirm(q)) m.mutate(!on);
          }}
          className={`btn-secondary ${TOUCH} disabled:opacity-60`}
        >
          {m.isPending ? 'Saving…' : on ? 'Stop carrying remainders' : 'Carry remainders'}
        </button>
      </div>
      {/* Never in silence: an operator who believes he has marked a party will
          read the next held-open trade as a bug in the sweep. */}
      <ErrorLine>{m.isError ? errorWords(m.error) : null}</ErrorLine>
    </Section>
  );
}

interface PartyWallet {
  id: number;
  chain: string;
  address: string;
  label: string | null;
  status: string;
  risk_score?: number | null;
  risk_checked_at?: string | null;
  retired_at?: string | null;
  /** When the operator archived it — no longer read (db/0064, status 'unwatched'). */
  unwatched_at?: string | null;
}

/** A Ledger account key behind this party's Bitcoin addresses — what the
 *  server says about it, never the key (see api.ts PartyDetail.wallet_keys). */
interface PartyWalletKey {
  registered: number;
  last_scanned_at: string | null;
  last_receive_used: number;
}

// admin.party_wallets' CHECK, every chain it admits, in its order — one list
// for this form and the Wallet register (lib/walletStates.ts says more).
const ALL_CHAINS = WALLET_CHAINS;

function WalletsSection({ partySlug, wallets, keys = [] }: { partySlug: string; wallets: PartyWallet[]; keys?: PartyWalletKey[] }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [chain, setChain] = useState<typeof ALL_CHAINS[number]>('TRC20');
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  // OFF BY DEFAULT (sweep item 37, 14 Sept 2026). Ticked by default, a second
  // wallet on the same chain silently archived the first — a party with two
  // live TRC20 addresses lost one to a box nobody read. Now the operator
  // ticks it, and the button says how many wallets will be retired.
  const [retirePrev, setRetirePrev] = useState(false);
  const othersOnChain = wallets.filter((w) => w.status === 'active' && w.chain === chain).length;
  const willRetire = retirePrev && othersOnChain > 0;

  const add = useMutation({
    mutationFn: () => api.addPartyWallet(partySlug, {
      chain, address, label: label || undefined,
      retire_previous_on_chain: willRetire,
    }),
    onSuccess: () => {
      tg()?.HapticFeedback.notificationOccurred('success');
      setAdding(false);
      setAddress(''); setLabel('');
      qc.invalidateQueries({ queryKey: ['party', partySlug] });
    },
    onError: () => tg()?.HapticFeedback.notificationOccurred('error'),
  });

  // Grouped by state, in the operator's words (lib/walletStates.ts): in use
  // and held first; retired (still watched) and archived (not watched, db/0064)
  // folded. Held rows used to fall into neither list and never showed.
  const current = wallets.filter(w => w.status === 'active' || w.status === 'flagged');
  const retired = wallets.filter(w => w.status === 'archived');
  const archived = wallets.filter(w => w.status === 'unwatched');

  return (
    <Section
      title="Wallets"
      icon={<Wallet size={14} />}
      accessory={
        adding
          ? <button onClick={() => setAdding(false)} className="btn-link text-meta max-lg:p-2.5 max-lg:-m-2.5">cancel</button>
          : <button onClick={() => setAdding(true)} className="btn-link text-meta max-lg:p-2.5 max-lg:-m-2.5">+ add</button>
      }
    >
      {adding && (
        <div className="space-y-2 py-2 mb-2 border-b border-tg-secondary">
          <div className="grid grid-cols-3 gap-1">
            {ALL_CHAINS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setChain(c)}
                className={`seg text-meta ${chain === c ? 'seg-on' : ''}`}
              >
                {c}
              </button>
            ))}
          </div>
          <Field label="Address">
            <input
              className="input w-full font-mono text-field lg:text-body"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </Field>
          <Field label="Label" hint="optional">
            <input
              className="input w-full text-field lg:text-body"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="May 2026 rotation"
            />
          </Field>
          {/* Only when there is something to retire — a first wallet has no
              "other" to lose. */}
          {othersOnChain > 0 && (
            <label className="flex items-center gap-2 text-meta">
              <input type="checkbox" checked={retirePrev} onChange={(e) => setRetirePrev(e.target.checked)} />
              <span className="text-tg-hint">
                Retire the other active {chain} wallet{othersOnChain === 1 ? '' : 's'} ({othersOnChain})
              </span>
            </label>
          )}
          <button
            className="btn-primary w-full text-body py-1.5"
            disabled={!address.trim() || add.isPending}
            onClick={() => add.mutate()}
          >
            {add.isPending ? 'Saving…'
              : willRetire ? `Save and retire ${othersOnChain} wallet${othersOnChain === 1 ? '' : 's'}`
              : 'Save wallet'}
          </button>
          <ErrorLine>{add.isError ? errorWords(add.error) : null}</ErrorLine>
        </div>
      )}

      {current.length === 0 && retired.length === 0 && archived.length === 0 && !adding && (
        <EmptyState className="!py-2">No wallets yet.</EmptyState>
      )}

      {/* THE LEDGER ACCOUNT KEY (18 Sept 2026). A desk Bitcoin wallet is a
          Ledger account: it hands out a new address after every transaction,
          and the sync derives each one from the account key before it is
          used. One line says so — how many addresses the key has registered,
          which receive address comes next, when the branches were last
          scanned. The key itself never reaches this screen. One line per
          account when a party holds several. */}
      {/* 22 Oct 2026: six words (it was seventeen); which receive address is next, and what the key is, stand one tap down. */}
      {keys.map((k, i) => (
        <StateLine
          key={i}
          className="py-1"
          more={<>Next receive 0/{k.last_receive_used + 1}. The addresses come from the Ledger account key — each one is derived before it is used.</>}
        >
          Ledger key · {k.registered} registered · {k.last_scanned_at ? `scanned ${fmtRelative(k.last_scanned_at)}` : 'not scanned'}
        </StateLine>
      ))}

      {current.map((w) => <WalletRow key={w.id} w={w} />)}

      {retired.length > 0 && (
        <details className="pt-2">
          <summary className="text-meta text-tg-hint cursor-pointer max-lg:py-2.5">
            {retired.length} retired — still watched
          </summary>
          <div className="opacity-60 pt-1">
            {retired.map((w) => <WalletRow key={w.id} w={w} />)}
          </div>
        </details>
      )}
      {archived.length > 0 && (
        <details className="pt-2">
          <summary className="text-meta text-tg-hint cursor-pointer max-lg:py-2.5">
            {archived.length} archived — not watched
          </summary>
          <div className="opacity-60 pt-1">
            {archived.map((w) => <WalletRow key={w.id} w={w} />)}
          </div>
        </details>
      )}
      {/* The door alone (21 Oct 2026 audit): the sentence "Retire or archive an
          address in the Wallet register." under every list is in About this
          screen. */}
      <p className="pt-2">
        <Link to="/wallets" className="btn-link text-meta max-lg:inline-block max-lg:py-2.5 max-lg:-my-2.5">Wallet register →</Link>
      </p>
    </Section>
  );
}

function WalletRow({ w }: { w: PartyWallet }) {
  const state = walletState(w.status);
  return (
    <div className="py-1.5 border-b border-tg-secondary last:border-b-0">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-body font-medium">{w.chain}{w.label && ` · ${w.label}`}</span>
        {/* A pill says an exception (21 Oct 2026 audit): Held, Retired, Archived.
            A wallet in use — the normal one — said "In use" in green on every row. */}
        {w.status !== 'active' && <Pill tone={state.tone}>{state.word}</Pill>}
      </div>
      <code className="font-mono text-meta text-tg-hint break-all">{w.address}</code>
      {w.risk_score != null && (
        <div className="text-meta mt-0.5 text-tg-hint">
          KYT risk: <span className="font-mono tabular text-tg-text">{w.risk_score}</span>
          {w.risk_checked_at && ` · ${fmtRelative(w.risk_checked_at)}`}
        </div>
      )}
      {w.status === 'archived' && w.retired_at && (
        <div className="text-meta mt-0.5 text-tg-hint">
          Retired {fmtRelative(w.retired_at)} · {chainIsRead(w.chain) ? 'transfers here still get matched' : watchWords(w.status, w.chain)}
        </div>
      )}
      {w.status === 'unwatched' && (
        <div className="text-meta mt-0.5 text-tg-hint">
          Archived{w.unwatched_at ? ` ${fmtRelative(w.unwatched_at)}` : ''} · no longer watched — money sent here is not seen
        </div>
      )}
    </div>
  );
}
